// Shared helpers for the Revenue analytics list and view pages.

/** Year to date: Jan 1 of this year through today. */
export const defaultRevenueRange = () => {
  const end = new Date();
  return { start: new Date(end.getFullYear(), 0, 1), end };
};

/** MM/DD/YYYY, the format the analytics endpoints accept. */
export const formatApiDate = (value) => {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${d.getFullYear()}`;
};

export const formatCurrency = (value) =>
  `$${(Number(value) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const formatPercent = (value) => (value === null || value === undefined ? '—' : `${value}%`);

export const formatDays = (value) => (value === null || value === undefined ? '—' : `${value} d`);

/** Net collection rate badge: 95%+ is the usual healthy benchmark, below 80% needs attention. */
export const NetCollectionBadge = ({ value }) => {
  if (value === null || value === undefined) return <span className="text-secondary">—</span>;
  const tone = value >= 95 ? 'success' : value >= 90 ? 'primary' : value >= 80 ? 'warning' : 'danger';
  return <span className={`badge rounded-pill text-bg-${tone}`}>{value}%</span>;
};

// Column ids match row keys so DataGrid sorts numerically.
const METRIC_COLUMNS = {
  visits: { id: 'visits', label: 'Visits', minWidth: 80, align: 'right', format: (_v, row) => (row.visits || 0).toLocaleString() },
  charges: { id: 'charges', label: 'Billed Charges', minWidth: 130, align: 'right', format: (_v, row) => formatCurrency(row.charges) },
  collected: { id: 'collected', label: 'Collected', minWidth: 120, align: 'right', format: (_v, row) => <strong>{formatCurrency(row.collected)}</strong> },
  adjustment: { id: 'adjustment', label: 'Adjustments', minWidth: 120, align: 'right', format: (_v, row) => formatCurrency(row.adjustment) },
  outstanding: { id: 'outstanding', label: 'Outstanding', minWidth: 120, align: 'right', format: (_v, row) => formatCurrency(row.outstanding) },
  net_collection_rate: { id: 'net_collection_rate', label: 'Net Coll. %', minWidth: 100, align: 'center', format: (_v, row) => <NetCollectionBadge value={row.net_collection_rate} /> },
  collected_per_visit: { id: 'collected_per_visit', label: 'Collected / Visit', minWidth: 130, align: 'right', format: (_v, row) => formatCurrency(row.collected_per_visit) },
  median_days_to_payment: { id: 'median_days_to_payment', label: 'Days to Pay', minWidth: 100, align: 'right', format: (_v, row) => formatDays(row.median_days_to_payment) },
  zero_paid_visits: { id: 'zero_paid_visits', label: '$0 Paid', minWidth: 80, align: 'right', format: (_v, row) => (row.zero_paid_visits || 0).toLocaleString() },
};

/** Metric columns for revenue tables, all of them by default or the named subset in order. */
export const revenueMetricColumns = (keys = Object.keys(METRIC_COLUMNS)) => keys.map((key) => METRIC_COLUMNS[key]);
